/* eslint-disable indent */
import { Common, color } from '@stockbit/ui';
import Link from 'next/link';
import { useMemo, useState } from 'react';
import auth from 'utils/auth';

import { effects as userEffects } from 'lib/entities/user';
import { useDispatch } from 'react-redux';
import {
  ModalBlockUnblockUser,
  ModalShareIdea,
  ModalSuspendUser,
} from 'global/Moderation/Modals';
import DATA_CY from 'constants/data-cy';
import { effects as watchlistEffects } from 'lib/entities/watchlist';
import { SOURCE_CHAT_ROOM_VIEWED } from 'features/chat/constant';
import { isAllowedToSuspend } from 'utils/userRole';
import {
  MenuWrapper,
  ProfileButton,
  ProfileButtonWrapper,
  ProfileMoreNav,
  ProfileTopHeaderButtonContainer,
} from '../styled';
import { UserGraph } from '../../../../@types/user';
import { mainProfileEvents } from '../analytics/ProfileAnalytics';

const { Flex, Button, IconWrapper, Iconmoon, Dropdown, Text, Menu } = Common;

interface TopHeaderProp {
  username: string;
  user: UserGraph;
}

function TopHeader({ username, user }: TopHeaderProp) {
  const dispatch = useDispatch();
  const { username: myUsername = '', privilege = 0 } =
    auth.getUserAccess() as unknown as {
      username: string;
      privilege: number;
    };
  const isSelf = username === myUsername;
  const allowedToSuspend = isAllowedToSuspend(privilege);

  const [loadingFollow, setLoadingFollow] = useState<boolean>(false);

  // modals state
  const [modalBlock, showModalBlock] = useState<boolean>(false);
  const [modalSuspend, showModalSuspend] = useState<boolean>(false);
  const [modalCompose, showModalCompose] = useState<boolean>(false);

  const handleFollowUser = async () => {
    const type = !user.status.is_followed ? 'FOLLOW' : 'UNFOLLOW';
    const params = {
      username,
      userid: user.user_id,
      type,
    };

    setLoadingFollow(true);

    // @ts-ignore
    const follow = await dispatch(watchlistEffects.followUser(params));

    // @ts-ignore
    const { data } = follow.payload;
    if (data) {
      if (type === 'FOLLOW') {
        mainProfileEvents.onUserProfileFollowSelected();
      } else {
        mainProfileEvents.onUserProfileUnfollowSelected();
      }

      setLoadingFollow(false);
    }
  };

  const handleToggleAlertUser = async () => {
    const data = {
      username,
      userid: user.user_id,
    };
    if (user?.status?.is_post_alert_enabled) {
      mainProfileEvents.onUserProfileTurnOffAlert();
    } else {
      mainProfileEvents.onUserProfileTurnOnAlert();
    }

    // @ts-ignore
    const alertReq = await dispatch(userEffects.setNotificationAlertUser(data));
    // @ts-ignore
    const { data: resData } = alertReq.payload;

    if (resData) {
      const { alert } = resData;
      if (alert) {
        mainProfileEvents.onUserProfileSuccessfullyTurnOnAlert();
      } else {
        mainProfileEvents.onUserProfileSuccessfullyTurnOffAlert();
      }
    }
  };

  const handlePostTo = async () => {
    showModalCompose(true);
    mainProfileEvents.onUserProfileComposePost();
  };

  const handleBlockUnblock = async () => {
    showModalBlock(true);
    if (user?.status?.is_blocked) {
      mainProfileEvents.onUserProfileUnblock();
    } else {
      mainProfileEvents.onUserProfileBlock();
    }
  };

  const menu = useMemo(() => {
    const listMenu = [];

    if (user?.status?.is_followed) {
      listMenu.push({
        key: 'profile-menu-item-1',
        color: 'inherit',
        label: `Turn ${
          user?.status?.is_post_alert_enabled ? 'off' : 'on'
        } alert for @${username} post`,
        onClick: handleToggleAlertUser,
      });
    }

    return [
      ...listMenu,
      {
        key: 'profile-menu-item-2',
        color: 'inherit',
        label: `Post To @${username}`,
        onClick: handlePostTo,
      },
      ...(!user.badge.is_admin
        ? [
            {
              key: 'profile-menu-item-4',
              color: `${color.sunsetOrange} !important`,
              label: `${
                user?.status?.is_blocked ? 'Unblock' : 'Block'
              } @${username}`,
              onClick: handleBlockUnblock,
            },
          ]
        : []),
      ...(!user.badge.is_admin && allowedToSuspend
        ? [
            {
              key: 'profile-menu-item-5',
              color: 'inherit',
              label: `Mute @${username}`,
              onClick: () => showModalSuspend(true),
            },
          ]
        : []),
    ];
  }, [user, allowedToSuspend]);

  const attributes = useMemo(() => {
    if (user?.status?.is_blocked) {
      return {
        icon: null,
        label: 'Unblock',
        strokeColor: 'gainsboro',
        fontColor: color.light,
        type: 'primary',
        onClick: handleBlockUnblock,
      };
    }

    if (user?.status?.is_followed) {
      return {
        icon: 'icon-toolbar_check',
        label: 'Following',
        strokeColor: color.primary,
        fontColor: color.primary,
        type: 'default',
        onClick: handleFollowUser,
      };
    }

    return {
      icon: 'icon-toolbar_plus',
      label: 'Follow',
      strokeColor: 'gainsboro',
      fontColor: color.light,
      type: 'primary',
      onClick: handleFollowUser,
    };
  }, [user]);

  return (
    <>
      <ProfileButtonWrapper>
        <ProfileTopHeaderButtonContainer>
          {isSelf ? (
            <Link href="/profile/edit">
              <a>
                <Button>
                  <IconWrapper>
                    <Text padding="0" color={color.primary}>
                      Edit Profile
                    </Text>
                  </IconWrapper>
                </Button>
              </a>
            </Link>
          ) : (
            <Flex align="center">
              <Link
                href={{
                  pathname: '/chat',
                  query: {
                    n: username,
                    source: SOURCE_CHAT_ROOM_VIEWED.findIndex(
                      (item) => item === 'User Profile',
                    ),
                  },
                }}
                passHref
              >
                <a
                  onClick={mainProfileEvents.onUserProfileChatSelected}
                  href="dummy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ProfileButton>
                    <Flex align="center">
                      <Iconmoon className="icon-stream_comment" />
                      <Text ml="8px" color={color.highgray}>
                        Chat
                      </Text>
                    </Flex>
                  </ProfileButton>
                </a>
              </Link>

              <Flex ml="10px">
                <ProfileButton
                  color={attributes.strokeColor}
                  loading={loadingFollow}
                  onClick={attributes.onClick}
                  type={attributes.type}
                >
                  <Flex align="center">
                    <Iconmoon
                      className={attributes.icon}
                      color={attributes.fontColor}
                      hoverColor={attributes.fontColor}
                    />
                    <Text color={attributes.fontColor}>{attributes.label}</Text>
                  </Flex>
                </ProfileButton>
              </Flex>

              <Flex ml="8px">
                <Dropdown
                  trigger="click"
                  overlay={
                    // eslint-disable-next-line react/jsx-wrap-multilines
                    <MenuWrapper>
                      {menu.map(({ label, color: textColor, onClick, key }) => (
                        <Menu.Item
                          onClick={onClick}
                          key={key}
                          style={{
                            padding: '0 4px',
                            height: 'auto',
                            lineHeight: 'unset',
                          }}
                          data-cy={
                            key === 'profile-menu-item-4'
                              ? DATA_CY.PROFILE.HEADER_BLOCK_BUTTON
                              : undefined
                          }
                        >
                          <Text py="4px" color={textColor}>
                            {label}
                          </Text>
                        </Menu.Item>
                      ))}
                    </MenuWrapper>
                  }
                >
                  <ProfileMoreNav data-cy={DATA_CY.PROFILE.HEADER_MORE_BUTTON}>
                    <Menu.MoreIcon />
                    <Menu.MoreIcon />
                    <Menu.MoreIcon />
                  </ProfileMoreNav>
                </Dropdown>
              </Flex>
            </Flex>
          )}
        </ProfileTopHeaderButtonContainer>
      </ProfileButtonWrapper>

      <ModalSuspendUser
        visible={modalSuspend}
        username={username}
        userId={user.user_id}
        postId={0}
        onCancel={() => showModalSuspend(false)}
      />
      <ModalBlockUnblockUser
        visible={modalBlock}
        username={username}
        userId={user.user_id}
        type={user?.status?.is_blocked ? 'unblock' : 'block'}
        onCancel={() => showModalBlock(false)}
      />
      <ModalShareIdea
        visible={modalCompose}
        username={username}
        onCancel={() => showModalCompose(false)}
      />
    </>
  );
}

export default TopHeader;
